import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customerService, { RestaurantSummary } from '../services/customer.service';
import LoadingAnimation from '../components/LoadingAnimation';
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  Search,
  Storefront as StorefrontIcon,
  Star as StarIcon,
  SortByAlpha as SortByAlphaIcon,
  LocationOn as LocationOnIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

type SortKey = 'recommended' | 'topRated' | 'nameAZ';

/**
 * Browse restaurants directly and order from their regular menu — the
 * Swiggy/Zomato-style flow, independent of the surprise-box "Browse Boxes"
 * page.
 */
export default function Restaurants() {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<RestaurantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('recommended');
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async (search?: string) => {
    setLoading(true);
    setError(false);
    try {
      const data = await customerService.getRestaurants(search);
      setRestaurants(data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setQuery(value);
    setCategory(null);
    load(value.trim() || undefined);
  };

  const categories = useMemo(() => {
    const seen = new Set<string>();
    restaurants.forEach((r) => {
      if (r.category) seen.add(r.category);
    });
    return Array.from(seen).sort();
  }, [restaurants]);

  // Filtering and ordering happen on the fetched list, so the chips stay put
  // and switching between them is instant.
  const visible = useMemo(() => {
    let list = category ? restaurants.filter((r) => r.category === category) : [...restaurants];
    if (sort === 'topRated') {
      list.sort((a, b) => (b.rating?.average ?? 0) - (a.rating?.average ?? 0));
    } else if (sort === 'nameAZ') {
      list.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    }
    return list;
  }, [restaurants, category, sort]);

  const toggleSort = (key: SortKey) => setSort((s) => (s === key ? 'recommended' : key));

  return (
    <Box>
      {/* Hero band */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #FFFCF5 0%, #F4EDE1 55%, #E9F1EB 100%)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3.5,
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Chip
            label="ORDER ANYTIME"
            size="small"
            sx={{
              bgcolor: '#FDEAE1',
              color: '#B5290A',
              fontWeight: 800,
              fontSize: 10,
              letterSpacing: 1,
              borderRadius: 999,
              border: 'none',
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            {visible.length} {visible.length === 1 ? 'restaurant' : 'restaurants'}
          </Typography>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          Restaurants near you
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
          Full menus, ordered and picked up your way.
        </Typography>

        <TextField
          fullWidth
          placeholder="Search restaurants or cuisines"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ maxWidth: 520, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2.5 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Sort + cuisine filters */}
      {!loading && restaurants.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<StarIcon sx={{ fontSize: 15 }} />}
            label="Top rated"
            onClick={() => toggleSort('topRated')}
            color={sort === 'topRated' ? 'primary' : 'default'}
            variant={sort === 'topRated' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            icon={<SortByAlphaIcon sx={{ fontSize: 15 }} />}
            label="A–Z"
            onClick={() => toggleSort('nameAZ')}
            color={sort === 'nameAZ' ? 'primary' : 'default'}
            variant={sort === 'nameAZ' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
          {categories.length > 0 && (
            <Box sx={{ width: '1px', height: 22, bgcolor: 'divider', mx: 0.5 }} />
          )}
          {categories.map((c) => (
            <Chip
              key={c}
              label={c}
              onClick={() => setCategory((current) => (current === c ? null : c))}
              color={category === c ? 'primary' : 'default'}
              variant={category === c ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700 }}
            />
          ))}
        </Box>
      )}

      {loading ? (
        <LoadingAnimation />
      ) : error ? (
        <Alert severity="error">Could not load restaurants. Please try again.</Alert>
      ) : visible.length === 0 ? (
        <Alert severity="info">No restaurants found. Try a different search or filter.</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 2.5,
          }}
        >
          {visible.map((restaurant) => {
            const image = restaurant.images?.[0] || restaurant.logo;
            const subtitle = [restaurant.category, restaurant.cuisineTags?.slice(0, 2).join(', ')]
              .filter(Boolean)
              .join(' · ');
            const rating = restaurant.rating?.average ?? 0;
            const topRated = rating >= 4.5;
            return (
              <Box
                key={restaurant._id}
                onClick={() => navigate(`/restaurants/${restaurant._id}`)}
                sx={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  transition: 'box-shadow 0.18s, transform 0.18s',
                  '&:hover': { boxShadow: '0 12px 30px rgba(35,31,22,0.14)', transform: 'translateY(-3px)' },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: 168,
                    bgcolor: '#F4EDE1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundImage: image ? `url(${image})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {!image && <StorefrontIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0) 55%, rgba(0,0,0,0.32) 100%)',
                    }}
                  />
                  {topRated && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        px: 1.1,
                        py: 0.4,
                        borderRadius: 999,
                        background: 'linear-gradient(135deg, #F88716, #B5290A)',
                        color: '#fff',
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: 0.8,
                      }}
                    >
                      TOP RATED
                    </Box>
                  )}
                  {restaurant.location?.city && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 14,
                        bottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                        color: '#fff',
                      }}
                    >
                      <LocationOnIcon sx={{ fontSize: 15 }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                        {restaurant.location.city}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 17, flex: 1, minWidth: 0 }} noWrap>
                      {restaurant.name}
                    </Typography>
                    {rating > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.3,
                          bgcolor: 'primary.main',
                          color: '#fff',
                          px: 0.8,
                          py: 0.3,
                          borderRadius: 1,
                        }}
                      >
                        <StarIcon sx={{ fontSize: 13 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                          {rating.toFixed(1)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {subtitle && (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.4 }}>
                      {subtitle}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <StorefrontIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.disabled" noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {restaurant.location?.address || 'Pickup available'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'primary.main' }}>
                      View menu
                    </Typography>
                    <ChevronRightIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
